import React, { useState, useEffect } from 'react';
import { useNotchedWhiteNoise } from '../hooks/useNotchedWhiteNoise';
import { useTherapyMilestoneTracking } from '../hooks/useTherapyMilestoneTracking';
import { analyticsEvents } from '../utils/analytics';
import { hapticFeedback } from '../utils/haptic';
import { isIOS } from '../utils/deviceDetection';

interface NotchedWhiteNoiseProps {
  tinnitusFrequency: number;
}

export const NotchedWhiteNoise: React.FC<NotchedWhiteNoiseProps> = ({
  tinnitusFrequency,
}) => {
  const [showSettings, setShowSettings] = useState(false);
  const [showIOSWarning, setShowIOSWarning] = useState(false);
  
  const {
    isPlaying,
    volume,
    notchFrequency,
    notchBandwidth,
    playNotchedNoise,
    stopNotchedNoise,
    setVolume,
    setNotchFrequency,
  } = useNotchedWhiteNoise(tinnitusFrequency, 0.2);

  // Milestone tracking
  const { startTracking, stopTracking } = useTherapyMilestoneTracking();

  useEffect(() => {
    // Only show warning for iOS users
    setShowIOSWarning(isIOS());
  }, []);

  const formatFrequency = (freq: number) => {
    if (freq >= 1000) {
      return `${(freq / 1000).toFixed(2)} kHz`;
    }
    return `${freq.toFixed(0)} Hz`;
  };

  return (
    <div className="bg-gradient-to-br from-purple-600 via-purple-500 to-pink-500 rounded-xl p-6 sm:p-8 shadow-2xl" role="region" aria-labelledby="notched-noise-heading">
      <div className="text-center mb-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2" id="notched-noise-heading">
          Your Sound Therapy
        </h2>
        <p className="text-white/90 text-base sm:text-lg">
          Research-based frequency removal for tinnitus relief
        </p>
      </div>

      {/* Frequency Visualization */}
      <div className="bg-white/10 backdrop-blur rounded-xl p-4 mb-6 text-center">
        {/* Mini Notch Visualizer */}
        <div className="flex items-center justify-center mb-3">
          {[...Array(20)].map((_, i) => {
            const barFreq = (i / 19) * 15000 + 1000; // Spread from 1kHz to 16kHz
            const isInNotch = Math.abs(barFreq - notchFrequency) <= notchBandwidth / 2;
            const height = isInNotch ? '4px' : '16px';
            const opacity = isPlaying && !isInNotch ? '1' : isInNotch ? '0.2' : '0.6';
            
            return (
              <div
                key={i}
                className="bg-white mx-0.5 transition-all duration-300 rounded-sm"
                style={{
                  height,
                  opacity,
                  transform: isPlaying && !isInNotch ? 'scale(1.1)' : 'scale(1)'
                }}
              />
            );
          })}
        </div>
        
        <div className="text-lg text-white mb-1">{formatFrequency(notchFrequency)}</div>
        <div className="text-sm text-white/70">
          Volume: {Math.round(volume * 200)}% • Research-Optimized Bandwidth
        </div>
        <div className="text-xs text-white/50 mt-1">
          Auto-calculated: {notchBandwidth} Hz (0.5 octave)
        </div>
      </div>
        

      {/* Controls */}
      <div className="space-y-4">
        {/* Play/Stop Button */}
        <div className="flex justify-center mb-6">
          {!isPlaying ? (
            <button
              onClick={() => {
                hapticFeedback.audioStart();
                playNotchedNoise();
                // Start milestone tracking for therapy session
                startTracking(notchFrequency);
                // Track additional interaction data
                analyticsEvents.trackFeatureInteraction('notched_noise', 'play', {
                  frequency: notchFrequency,
                  bandwidth: notchBandwidth,
                  volume: volume
                });
              }}
              className="w-full max-w-md py-6 px-8 bg-white text-purple-600 font-bold text-xl rounded-full hover:bg-gray-50 transition-all duration-200 shadow-lg transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-white/50"
              aria-label="Start sound therapy"
            >
              ▶ Start Sound Therapy
            </button>
          ) : (
            <button
              onClick={() => {
                hapticFeedback.audioStop();
                stopNotchedNoise();
                // Stop milestone tracking
                stopTracking();
                // Track therapy stop interaction
                analyticsEvents.trackFeatureInteraction('notched_noise', 'stop', {
                  frequency: notchFrequency
                });
              }}
              className="w-full max-w-md py-6 px-8 bg-white/90 text-red-600 font-bold text-xl rounded-full hover:bg-white transition-all duration-200 shadow-lg transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-white/50"
              aria-label="Stop sound therapy"
            >
              ⏸ Stop Therapy
            </button>
          )}
        </div>

        {/* Settings Toggle */}
        <div className="text-center mb-4">
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="text-white/80 hover:text-white text-sm font-medium transition-colors"
          >
            {showSettings ? 'Hide Settings ▲' : 'Adjust Settings ▼'}
          </button>
        </div>

        {/* Expandable Settings */}
        {showSettings && (
          <div className="space-y-4">
            {/* Frequency Control */}
            <div className="bg-white/10 backdrop-blur rounded-xl p-4">
              <h3 className="text-white font-medium mb-3 text-center">Frequency</h3>
              
              {/* Direct Input */}
              <div className="mb-4">
                <input
                  type="number"
                  value={notchFrequency}
                  onChange={(e) => {
                    const freq = parseInt(e.target.value);
                    if (!isNaN(freq) && freq >= 20 && freq <= 20000) {
                      setNotchFrequency(freq);
                      analyticsEvents.trackFeatureInteraction('notched_noise', 'frequency_set_direct', freq);
                    }
                  }}
                  className="w-full p-3 text-center text-lg font-bold bg-white/10 border-2 border-white/30 rounded-lg text-white placeholder-white/50 focus:border-white focus:bg-white/20 focus:outline-none transition-all"
                  min="20"
                  max="20000"
                  placeholder="Enter frequency"
                />
                <p className="text-white/70 text-xs text-center mt-1">
                  Enter exact frequency (20-20,000 Hz)
                </p>
              </div>

              {/* Preset Buttons */}
              <div className="grid grid-cols-4 gap-2 mb-4">
                {[2000, 4000, 6000, 8000].map(freq => (
                  <button
                    key={freq}
                    onClick={() => {
                      setNotchFrequency(freq);
                      analyticsEvents.trackFeatureInteraction('notched_noise', 'frequency_preset', freq);
                    }}
                    className={`py-2 px-3 rounded-lg font-medium transition-all transform hover:scale-105 ${
                      notchFrequency === freq
                        ? 'bg-white text-purple-600 shadow-lg'
                        : 'bg-white/20 hover:bg-white/30 text-white'
                    }`}
                  >
                    {freq}
                  </button>
                ))}
              </div>

              {/* Fine Adjustments */}
              <div className="grid grid-cols-4 gap-1">
                <button
                  onClick={() => setNotchFrequency(Math.max(20, notchFrequency - 100))}
                  className="py-1 px-2 bg-white/20 hover:bg-white/30 rounded text-white text-xs font-medium transition-colors"
                >
                  -100
                </button>
                <button
                  onClick={() => setNotchFrequency(Math.max(20, notchFrequency - 10))}
                  className="py-1 px-2 bg-white/20 hover:bg-white/30 rounded text-white text-xs font-medium transition-colors"
                >
                  -10
                </button>
                <button
                  onClick={() => setNotchFrequency(Math.min(20000, notchFrequency + 10))}
                  className="py-1 px-2 bg-white/20 hover:bg-white/30 rounded text-white text-xs font-medium transition-colors"
                >
                  +10
                </button>
                <button
                  onClick={() => setNotchFrequency(Math.min(20000, notchFrequency + 100))}
                  className="py-1 px-2 bg-white/20 hover:bg-white/30 rounded text-white text-xs font-medium transition-colors"
                >
                  +100
                </button>
              </div>
            </div>

            {/* Volume Control */}
            <div className="bg-white/10 backdrop-blur rounded-xl p-4">
              <h3 className="text-white font-medium mb-3 text-center">Volume</h3>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setVolume(Math.max(0, volume - 0.05))}
                  className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center text-white font-bold transition-colors flex-shrink-0"
                >
                  −
                </button>
                <div className="flex-1 flex flex-col justify-center">
                  <div className="flex items-center justify-center mb-1">
                    <span className="text-white text-sm font-medium">{Math.round(volume * 200)}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="0.5"
                    step="0.01"
                    value={volume}
                    onChange={(e) => {
                      const newVolume = parseFloat(e.target.value);
                      setVolume(newVolume);
                      analyticsEvents.trackFeatureInteraction('notched_noise', 'volume_adjusted', Math.round(newVolume * 200));
                    }}
                    className="w-full therapy-slider"
                  />
                </div>
                <button
                  onClick={() => setVolume(Math.min(0.5, volume + 0.05))}
                  className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center text-white font-bold transition-colors flex-shrink-0"
                >
                  +
                </button>
              </div>
              <p className="text-white/70 text-xs text-center mt-2">Keep it quiet</p>
            </div>

          </div>
        )}
      </div>

      {/* Research Information / iOS Warning */}
      <div className="mt-6 bg-white/10 backdrop-blur rounded-xl p-4">
        {showIOSWarning ? (
          <>
            <h3 className="text-white font-medium mb-3 text-center">Important for iPhone/iPad</h3>
            <div className="text-sm text-white/90 text-center">
              <strong>Keep your screen on during therapy.</strong> Audio stops when screen locks on iOS devices.
            </div>
          </>
        ) : (
          <>
            <h3 className="text-white font-medium mb-3 text-center">Research-Based Therapy</h3>
            <div className="text-sm text-white/90 text-center mb-3">
              This therapy uses a <strong>0.5 octave bandwidth</strong> automatically calculated for your frequency,
              based on clinical studies showing <strong>20% reduction in tinnitus loudness</strong>.
            </div>
            <div className="grid md:grid-cols-3 gap-3 text-sm text-white/80">
              <div className="flex items-center gap-2">
                <span className="text-white">✓</span>
                <span>Use 1-2 hours daily</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-white">✓</span>
                <span>Keep volume comfortable</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-white">✓</span>
                <span>Consistent daily use</span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};